import HStack from "@components/layout/HStack";
import VStack from "@components/layout/VStack";
import Tooltip from "@components/ui/Tooltip";
import { tooltipText } from "@shared/constants/appConstants";
import { routes } from "@shared/constants/routes";
import {
  useAppConfigStore,
  useAppointmentStore,
  useAttendeesStore,
  useFindColleagueStore,
  useOutlookStore,
  useResourceStore,
  useSettingStore,
  useThemeStore,
} from "@shared/stores";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
// import Toaster from "@components/ui/Toaster";
import { CategoryOperator, ComparisonOperator, FindFavoritePageType, SortBy } from "@shared/types/enums/globalEnum";
// import { handleGetInitSearchPath } from "../../shared/helpers/handleGetInitSearchPath";
import { initBroadcast } from "../../lib/utils/broadcast";
import { IExpandDialogData } from "../../shared/types/expandDialog";
import { useMainTemplate } from "../hooks/useMainTemplate";
import {
  DebugIcon,
  ExpandIcon,
  IconAIAgent,
  LikeIcon,
  MagnifyingGlass,
  PeopleSearchIcon,
  ResourceIcon,
  SettingsIcon,
  StarIcon,
} from "../icons/IconCustom";
import Button from "../ui/Button";
import { useAuthStore } from "../../features/auth/store/authStore";

type Props = {
  children: React.ReactNode;
};

const MainTemplate = ({ children }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const { selectedResource, filterResource, deskLocationList, meetingLabel: meetingLabelResource } = useResourceStore();
  const { isDarkMode, themeId } = useThemeStore();
  const {
    recommendationScreen,
    tileConfiguration,
    defaultResourceType,
    defaultLocations,
    aiAssistant,
    dateFormat,
    timeFormat,
    firstDayOfWeek,
    dateSeparator,
    capacityFilter,
  } = useSettingStore();

  const [broadcastInitialized, setBroadcastInitialized] = useState<boolean>(false);
  const { handleOpenDialogExpand } = useMainTemplate();
  const { requiredAttendees, optionalAttendees, mainAttendee, locationUnknown } = useAttendeesStore();
  const {
    startDateTimeZone,
    endDateTimeZone,
    timeZone,
    iCalUId,
    customUid,
    recurringDates,
    startTime,
    endTime,
    globalId,
    isReopenAppointment,
    isEditingRecurringSeries,
    isNoEndDateRecurring,
    recurrence,
  } = useOutlookStore();
  const { startDateTimeUtc, endDateTimeUtc } = useAppointmentStore();
  const {
    language,
    locations,
    categories,
    resourceTypes,
    helpUrlForOutLookAddin,
    parameters,
    meetingLabel,
    defaultMeetingLabel,
  } = useAppConfigStore();
  const [searchParams] = useSearchParams();
  const { auth } = useAuthStore();

  const isFirstMount = useRef<boolean>(true);
  const isStartPage = useMemo(() => pathname === routes.start, [pathname]);

  const handleNavigate = (route: string) => {
    navigate(route);
  };

  const handleOpenDialog = (url: string) => {
    const resourceTypeFilter = searchParams.get("resourceType") || defaultResourceType;
    const currentResourceType = defaultLocations.defaultResourceTypes.find((i) => i.type === resourceTypeFilter);
    const locationFilter = currentResourceType?.locationIds?.split(",").map(Number) || [];
    // let meetingsData = null;
    // if (pathname === routes.findColleague && meetings.length > 0) {
    //   meetingsData = JSON.parse(JSON.stringify(meetings));
    // }

    const data: IExpandDialogData = {
      selectedResources: selectedResource.map((resource) => resource.toJson()),
      requiredAttendees: requiredAttendees.map((attendee) => attendee.toJson()),
      optionalAttendees: optionalAttendees.map((attendee) => attendee.toJson()),
      mainAttendee: mainAttendee?.toJson() || null,
      locationUnknown: locationUnknown,
      recurringDates: recurringDates,
      recurrence: recurrence,
      timeZone: timeZone || "",
      startDateTimeZone: startDateTimeZone,
      endDateTimeZone: endDateTimeZone,
      startDateTimeUtc: startDateTimeUtc,
      endDateTimeUtc: endDateTimeUtc,
      startTime: startTime?.toISOString() || "",
      endTime: endTime?.toISOString() || "",
      iCalUId: iCalUId || "",
      globalId: globalId || "",
      customUid: customUid || "",
      isDarkMode: isDarkMode,
      themeId: themeId,
      firstDayOfWeek: firstDayOfWeek,
      dateSeparator: dateSeparator,
      timeFormat: timeFormat,
      dateFormat: dateFormat,
      language: language,
      tileConfiguration: JSON.parse(JSON.stringify(tileConfiguration)),
      defaultResourceType,
      defaultLocations: JSON.parse(JSON.stringify(defaultLocations)),
      locations: JSON.parse(JSON.stringify(locations)),
      categories: JSON.parse(JSON.stringify(categories)),
      resourceTypes: JSON.parse(JSON.stringify(resourceTypes)),
      helpUrlForOutLookAddin: helpUrlForOutLookAddin,
      parameters: JSON.parse(JSON.stringify(parameters)),
      meetingLabel: JSON.parse(JSON.stringify(meetingLabel)) || null,
      defaultMeetingLabel: JSON.parse(JSON.stringify(defaultMeetingLabel)) || null,
      deskLocationList: JSON.parse(JSON.stringify(deskLocationList)),
      meetingLabelResource: meetingLabelResource || null,
      filterResource: {
        resourceType: filterResource?.resourceType || resourceTypeFilter,
        locations: filterResource?.locations?.length ? filterResource.locations : locationFilter,
        textSearch: filterResource?.textSearch ?? "",
        status: filterResource?.status ?? true,
        capacityOperator: filterResource?.capacityOperator ?? ComparisonOperator.MoreThan,
        capacityValue: filterResource?.capacityValue ?? 0,
        sortBy: filterResource?.sortBy ?? SortBy.NameAsc,
        categories: filterResource?.categories?.length ? filterResource.categories : [],
        categoryOperator: filterResource?.categoryOperator || CategoryOperator.Or,
        isChangedLocations: filterResource?.isChangedLocations ?? false,
      },
      meetings: null,
      isNoEndDateRecurring: isNoEndDateRecurring || null,
      capacityFilter,
      colleagueSelected: null,
      tokens: auth?.tokens || null,
    };
    handleOpenDialogExpand(url, data);
  };

  useEffect(() => {
    if (!broadcastInitialized) {
      initBroadcast();
      setBroadcastInitialized(true);
    }
  }, [broadcastInitialized]);

  useEffect(() => {
    // Bỏ qua lần đầu tiên khi component mount
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (selectedResource.length > 0) {
      navigate(routes.selectedResources);
    }
  }, [selectedResource]);

  if (isStartPage) {
    return <div className="w-full h-screen overflow-hidden">{children}</div>;
  }

  return (
    <VStack className={`h-screen bg-veryLightGray dark:bg-darkBackgroundColor dark:text-white`}>
      {/* Action */}
      <HStack
        className={`w-full py-2 px-4 ${isDarkMode ? "dark:bg-darkBackgroundColor" : "bg-bgLinearGradientBlue"}`}
        justifyContent="between"
      >
        <HStack gap={2}>
          {/* Recommendation */}
          {recommendationScreen && (
            <Tooltip message={t(tooltipText.recommend)} position="bottom">
              <Button
                leftIcon={<LikeIcon isDarkMode={isDarkMode} isActive={pathname === routes.recommendation} />}
                onClick={() => handleNavigate(routes.recommendation)}
              />
            </Tooltip>
          )}

          {/* Search */}
          <Tooltip message={t(tooltipText.search)} position="bottom">
            <Button
              leftIcon={<MagnifyingGlass isDarkMode={isDarkMode} isActive={pathname === routes.search} />}
              // onClick={() => handleNavigate(routes.search + "?isUseLocationDefault=true&isUseCategoryDefault=true")}
              onClick={() => handleNavigate(routes.search)}
            />
          </Tooltip>

          {/* Favorite */}
          <Tooltip message={t(tooltipText.favorite)} position="bottom">
            <Button
              leftIcon={<StarIcon isDarkMode={isDarkMode} isActive={pathname === routes.findFavorite} />}
              onClick={() => handleNavigate(routes.findFavorite + "?type=" + FindFavoritePageType.Favorite)}
            />
          </Tooltip>

          {/* Resource Selected */}

          <Tooltip message={t(tooltipText.resourceSelected)} position="bottom">
            <div className="relative">
              <Button
                leftIcon={<ResourceIcon isDarkMode={isDarkMode} isActive={pathname === routes.selectedResources} />}
                onClick={() => handleNavigate(routes.selectedResources)}
              />
              {selectedResource.length > 0 ? (
                <div className="absolute top-[-2px] right-[-2px] w-[10px] h-[10px] bg-strongGreen rounded-full" />
              ) : null}
            </div>
          </Tooltip>

          {/* Agent */}
          {aiAssistant && (
            <Tooltip message={t(tooltipText.agent)} position="bottom">
              <Button
                leftIcon={<IconAIAgent isDarkMode={isDarkMode} isActive={pathname === routes.agent} />}
                onClick={() => handleNavigate(routes.agent)}
              />
            </Tooltip>
          )}

          {/* Find Colleague */}
          <Tooltip
            message={
              (!isReopenAppointment && recurringDates?.length > 0) || (isReopenAppointment && isEditingRecurringSeries)
                ? t(tooltipText.findColleagueNoSupportRecurring)
                : t(tooltipText.findColleague)
            }
            position="bottom"
          >
            <Button
              disabled={
                (!isReopenAppointment && recurringDates?.length > 0) ||
                (isReopenAppointment && isEditingRecurringSeries)
              }
              leftIcon={<PeopleSearchIcon isDarkMode={isDarkMode} isActive={pathname === routes.findColleague} />}
              onClick={() => handleNavigate(routes.findColleague)}
            />
          </Tooltip>

          {/* Floor Plan */}
          {/* <Tooltip message={t(tooltipText.floorPlan)} position="bottom">
          <Button
            leftIcon={<MapIcon isDarkMode={isDarkMode} />}
            onClick={() => {
              const dialogUrl = `${window.location.origin}/floorplan.html?dialog=true`;
              handleOpenDialog(dialogUrl);
            }}
          />
        </Tooltip> */}
        </HStack>

        <HStack gap={2}>
          {/* <Tooltip message={t(tooltipText.testAuth)} position="bottom">
            <Button
              leftIcon={<DebugIcon isDarkMode={isDarkMode} isActive={pathname === routes.testAuth} />}
              onClick={() => handleNavigate(routes.testAuth)}
            />
          </Tooltip> */}

          {/* Setting */}
          <Tooltip message={t(tooltipText.setting)} position="bottom">
            <Button
              leftIcon={<SettingsIcon isDarkMode={isDarkMode} isActive={pathname === routes.setting} />}
              onClick={() => handleNavigate(routes.setting)}
            />
          </Tooltip>

          {/* Expand */}
          {parameters.floorplan ? (
            <Tooltip message={t(tooltipText.floorPlan)} position="bottom">
              <Button
                onClick={() => {
                  let origin = window.location.origin;
                  if (!origin.includes("localhost") && !origin.includes("client-app-2.pages.dev")) {
                    origin = `${origin}/ClientApps`;
                  }
                  const dialogUrl = `${origin}/expand.html?dialog=true`;
                  handleOpenDialog(dialogUrl);
                }}
                leftIcon={<ExpandIcon isDarkMode={isDarkMode} />}
              />
            </Tooltip>
          ) : null}
        </HStack>
      </HStack>

      {/* {isActiveWarning ? <Toaster /> : null} */}

      {/* Children */}
      <div className="w-full flex-1 overflow-hidden">{children}</div>
    </VStack>
  );
};

export default MainTemplate;
